import React from 'react';

/**
 * Lightweight Markdown for Intelligence chat bubbles.
 * Supports: paragraphs, soft breaks, **bold**, *italic*, `code`,
 * unordered lists (*, -, •), ordered lists (1.).
 * No raw HTML — model output is treated as text with Markdown markers only.
 */

type InlinePart =
  | { kind: 'text'; value: string }
  | { kind: 'bold'; value: string }
  | { kind: 'italic'; value: string }
  | { kind: 'code'; value: string };

const INLINE_RE = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;

function parseInline(text: string): InlinePart[] {
  const parts: InlinePart[] = [];
  let last = 0;
  const re = new RegExp(INLINE_RE.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) {
      parts.push({ kind: 'text', value: text.slice(last, m.index) });
    }
    const token = m[0];
    if (token.startsWith('**') && token.endsWith('**')) {
      parts.push({ kind: 'bold', value: token.slice(2, -2) });
    } else if (token.startsWith('`') && token.endsWith('`')) {
      parts.push({ kind: 'code', value: token.slice(1, -1) });
    } else if (token.startsWith('*') && token.endsWith('*')) {
      parts.push({ kind: 'italic', value: token.slice(1, -1) });
    } else {
      parts.push({ kind: 'text', value: token });
    }
    last = m.index + token.length;
  }
  if (last < text.length) {
    parts.push({ kind: 'text', value: text.slice(last) });
  }
  return parts.length ? parts : [{ kind: 'text', value: text }];
}

function Inline({ text }: { text: string }) {
  return (
    <>
      {parseInline(text).map((part, i) => {
        if (part.kind === 'bold') return <strong key={i}>{part.value}</strong>;
        if (part.kind === 'italic') return <em key={i}>{part.value}</em>;
        if (part.kind === 'code') return <code key={i}>{part.value}</code>;
        return <React.Fragment key={i}>{part.value}</React.Fragment>;
      })}
    </>
  );
}

type Block =
  | { kind: 'p'; lines: string[] }
  | { kind: 'ul'; items: string[] }
  | { kind: 'ol'; items: string[] };

const UL_RE = /^[\*\-•]\s+(.+)$/;
const OL_RE = /^\d+\.\s+(.+)$/;

function parseBlocks(src: string): Block[] {
  const lines = (src || '').replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i += 1;
      continue;
    }

    const ul = line.match(UL_RE);
    if (ul) {
      const items: string[] = [];
      while (i < lines.length) {
        const m = lines[i].match(UL_RE);
        if (!m) break;
        items.push(m[1]);
        i += 1;
      }
      blocks.push({ kind: 'ul', items });
      continue;
    }

    const ol = line.match(OL_RE);
    if (ol) {
      const items: string[] = [];
      while (i < lines.length) {
        const m = lines[i].match(OL_RE);
        if (!m) break;
        items.push(m[1]);
        i += 1;
      }
      blocks.push({ kind: 'ol', items });
      continue;
    }

    const para: string[] = [];
    while (i < lines.length && lines[i].trim()) {
      if (UL_RE.test(lines[i]) || OL_RE.test(lines[i])) break;
      para.push(lines[i]);
      i += 1;
    }
    blocks.push({ kind: 'p', lines: para });
  }

  return blocks;
}

interface ChatMarkdownProps {
  content: string;
  className?: string;
}

const ChatMarkdown: React.FC<ChatMarkdownProps> = ({ content, className }) => {
  const blocks = parseBlocks(content);
  if (!blocks.length) {
    return <div className={className}>{content}</div>;
  }

  return (
    <div className={className}>
      {blocks.map((block, bi) => {
        if (block.kind === 'ul') {
          return (
            <ul key={bi}>
              {block.items.map((item, ii) => (
                <li key={ii}>
                  <Inline text={item} />
                </li>
              ))}
            </ul>
          );
        }
        if (block.kind === 'ol') {
          return (
            <ol key={bi}>
              {block.items.map((item, ii) => (
                <li key={ii}>
                  <Inline text={item} />
                </li>
              ))}
            </ol>
          );
        }
        return (
          <p key={bi}>
            {block.lines.map((line, li) => (
              <React.Fragment key={li}>
                {li > 0 ? <br /> : null}
                <Inline text={line} />
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
};

export default ChatMarkdown;
